import {
  createEntityAdapter,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit'
import { ITenantUser, UsersPageState } from './typings'
import {
  createUser,
  deleteUser,
  getSalesTeamUserList,
  getUser,
  getUserEmailUniqueness,
  getUserList,
  getUserListSearch,
  updateUser,
} from './api'
import { StoreData } from '../reducers/typings'
import { loadFilterPreferences } from '@user/store/userPreferences'

const userAdapter = createEntityAdapter<ITenantUser>({
  selectId: (user) => user.id,
  sortComparer: (a, b) => a.emailAddress?.localeCompare(b.emailAddress),
})

export const initialState = userAdapter.getInitialState<UsersPageState>({
  page: 1,
  perPage: 10,
  totalCount: 0,
  orderBy: 'employee desc',
  emailUniquenessByUserId: {},
  pageIds: [],
  pageUsers: {},
  search: {
    value: '',
    placeholder: 'Search...',
    searchFields: [
      {
        propertyNames: ['firstName', 'lastName'],
        matchType: 'contains',
      },
    ],
  },
})

const users = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsersOrderBy: (state, { payload }: PayloadAction<string>) => {
      state.orderBy = payload
    },
    setUsersPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload
    },
    setUsersPerPage: (state, { payload }: PayloadAction<number>) => {
      state.perPage = payload
    },
    setUserActiveRecordCount: (state, { payload }) => {
      const user = state.entities[payload.id]
      if (user) {
        user.openLeadCount = payload.openLeadCount
        user.openOppCount = payload.openOppCount
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSalesTeamUserList.fulfilled, (state, { payload }) => {
      payload.map((user) => {
        user.firstName = user.firstName || 'Missing Name'
        user.lastName = user.lastName || 'In PestPac'
      })
      userAdapter.setAll(state, payload)
    })
    builder.addCase(createUser.fulfilled, (state, { payload }) => {
      payload.firstName = payload.firstName || 'Missing Name'
      payload.lastName = payload.lastName || 'In PestPac'
      userAdapter.addOne(state, payload)
    })
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      payload.firstName = payload.firstName || 'Missing Name'
      payload.lastName = payload.lastName || 'In PestPac'
      userAdapter.upsertOne(state, payload)
    })
    builder.addCase(updateUser.fulfilled, (state, { payload }) => {
      payload.firstName = payload.firstName || 'Missing Name'
      payload.lastName = payload.lastName || 'In PestPac'
      userAdapter.upsertOne(state, payload)
    })
    builder.addCase(deleteUser.fulfilled, (state, { payload }) => {
      userAdapter.removeOne(state, payload)
      state.pageIds = state.pageIds.filter((id) => id !== payload)
      delete state.pageUsers[payload]
      state.totalCount -= 1
    })
    builder.addCase(getUserEmailUniqueness.fulfilled, (state, { payload }) => {
      userAdapter.updateMany(
        state,
        payload.map(({ userId, emailIsUnique }) => ({
          id: userId,
          changes: { emailIsUnique },
        })),
      )

      payload.forEach(({ userId, emailIsUnique }) => {
        state.emailUniquenessByUserId[userId] = emailIsUnique
      })
    })
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const userPref = payload.find((p) => p.type === 'users')
      state.orderBy = userPref
        ? JSON.parse(userPref.filter).orderBy
        : 'employee desc'
      return state
    })
    // Users grid requests (forGrid) own the page. Only the latest one may
    // write, so a slow earlier response can't replace a newer sort or page.
    builder.addMatcher(
      isAnyOf(getUserList.pending, getUserListSearch.pending),
      (state, { meta }) => {
        if (meta.arg.forGrid) state.pageRequestId = meta.requestId
      },
    )
    builder.addMatcher(
      isAnyOf(getUserList.fulfilled, getUserListSearch.fulfilled),
      (state, { payload, meta }) => {
        const forGrid = !!meta.arg.forGrid
        if (forGrid && meta.requestId !== state.pageRequestId) return

        payload.items.forEach((user) => {
          user.firstName = user.firstName || 'Missing Name'
          user.lastName = user.lastName || 'In PestPac'
          user.emailIsUnique = state.emailUniquenessByUserId[user.id]
        })
        userAdapter.setAll(state, payload.items)
        if (!forGrid) return

        state.totalCount = payload.count
        state.pageIds = payload.items.map((user) => user.id)
        state.pageUsers = Object.fromEntries(
          payload.items.map((user) => [user.id, user]),
        )
      },
    )
    // Show an empty grid rather than the old rows under the new sort/page
    builder.addMatcher(
      isAnyOf(getUserList.rejected, getUserListSearch.rejected),
      (state, { meta }) => {
        if (!meta.arg.forGrid || meta.requestId !== state.pageRequestId) return
        state.pageIds = []
        state.pageUsers = {}
        state.totalCount = 0
      },
    )
  },
})

export const {
  setUsersOrderBy,
  setUsersPage,
  setUsersPerPage,
  setUserActiveRecordCount,
} = users.actions

export default users.reducer

export const userAdapterSelectors = userAdapter.getSelectors<StoreData>(
  (state) => state.users,
)

export const { selectAll: selectAllUsers, selectById: selectUserById } =
  userAdapterSelectors

export const selectUsersFilters = (state: StoreData) => {
  return state.users
}

/**
 * The Users grid page in server sort order. Prefers the live entity (it picks
 * up edits) and falls back to the page snapshot when another screen's load
 * replaced the entities.
 */
export const selectUsersPage = createSelector(
  [
    (state: StoreData) => state.users.pageIds,
    (state: StoreData) => state.users.pageUsers,
    (state: StoreData) => state.users.entities,
  ],
  (pageIds, pageUsers, entities) =>
    pageIds
      .map((id) => entities[id] ?? pageUsers[id])
      .filter((user): user is ITenantUser => !!user),
)

export const selectUsersTotalCount = (state: StoreData) =>
  state.users.totalCount

export const selectEmailUniquenessByUserId =
  (userId: string) =>
  (state: StoreData): boolean | undefined =>
    state.users.emailUniquenessByUserId[userId]

export const selectEmailUniquenessMap = (state: StoreData) =>
  state.users.emailUniquenessByUserId
